/**
 * External Imports
 */
import { FC, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';

/**
 * Nexus Imports
 */
import { NeuPagination, NeuTable } from '@neutron/react';

/**
 * Internal Imports
 */
import Loading from '../Loading';
import NoDataSplashView from './noDataSplashView';
import {
  setUsersPage,
  setEmployeeFreqReportPage,
  setEmployeeRoundsReportPage,
  setPatientRoundsReportPage,
  setCurrentStoplightPage,
  setCurrentFeedbackPage,
  setCurrentIssuePage,
  setCensusReportPage
} from '../../../redux/actions';

/**
 * Style Imports
 */
import {
  TableHeader,
  TableContent,
  TableContentContainer,
  TableFooter,
  StyledNeuTableRow,
  StyledLabel
} from './Table.styles';
import { RootState } from '../../../redux/store';

const Table: FC<{
  headers: JSX.Element[];
  rows: JSX.Element[] | (boolean | JSX.Element)[];
  customClass?: string;
  isTableEmpty?: boolean;
  isTableEmptySearch?: boolean;
  isTableEmptyCompleted?: boolean;
  isAdminAreaUpdatesOnly?: boolean;
  loading?: boolean;
  offset?: string;
  selectedTab?: string;
  totalResults?: number;
  type?: string;
  onSort?: any;
  currentStoplightPage: number;
  currentIssuePage: number;
  currentFeedbackPage: number;
  currentPatientRoundsReportPage: number;
  currentEmployeeRoundsReportPage: number;
  currentEmployeeFreqReportPage: number;
  currentUsersPage: number;
  currentCensusReportPage: number;
}> = ({
  headers = [],
  rows = [],
  customClass = '',
  isAdminAreaUpdatesOnly = false,
  isTableEmpty = false,
  isTableEmptySearch = false,
  isTableEmptyCompleted = false,
  loading,
  offset,
  selectedTab,
  totalResults,
  type,
  onSort,
  currentStoplightPage,
  currentIssuePage,
  currentFeedbackPage,
  currentPatientRoundsReportPage,
  currentEmployeeRoundsReportPage,
  currentEmployeeFreqReportPage,
  currentUsersPage,
  currentCensusReportPage
}) => {
  const dispatch = useDispatch();

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(50);

  const hasBackendPagination = useMemo(() => {
    return (
      type === 'stoplight' ||
      type === 'feedback' ||
      type === 'issue' ||
      type === 'patientRoundsReport' ||
      type === 'patientCensusReport' ||
      type === 'employeeRoundsReport' ||
      type === 'employeeFrequencyReport' ||
      type === 'users'
    );
  }, [type]);

  const loadingLabel = useMemo(() => {
    switch (type) {
      case 'employeeList':
      case 'adHocEmployeeList':
      case 'my-employee-list':
      case 'delegate-list':
      case 'delegated-to-me':
        return 'employees';
      case 'patientCensus':
        return 'patients';
      case 'feedback':
      case 'templates':
      case 'users':
        return type;
      case 'issue':
      case 'stoplight':
        return `${type}s`;
      case 'cnedList':
      case 'cscList':
      case 'validationList':
      case 'supportList':
        return 'subjects';
      case 'patientComplianceReport':
      case 'patientRoundsReport':
      case 'patientCensusReport':
      case 'employeeRoundsReport':
      case 'employeeFrequencyReport':
        return 'report';
      case 'questionBank':
        return 'questions';
      default:
        return '';
    }
  }, [type]);

  const pageNumberChanged = (pageNum: number) => {
    if (type === 'stoplight') {
      dispatch(setCurrentStoplightPage(pageNum));
    } else if (type === 'feedback') {
      dispatch(setCurrentFeedbackPage(pageNum));
    } else if (type === 'issue') {
      dispatch(setCurrentIssuePage(pageNum));
    } else if (type === 'patientRoundsReport') {
      dispatch(setPatientRoundsReportPage(pageNum));
    } else if (type === 'patientCensusReport') {
      dispatch(setCensusReportPage(pageNum));
    } else if (type === 'employeeRoundsReport') {
      dispatch(setEmployeeRoundsReportPage(pageNum));
    } else if (type === 'employeeFrequencyReport') {
      dispatch(setEmployeeFreqReportPage(pageNum));
    } else if (type === 'users') {
      dispatch(setUsersPage(pageNum));
    }

    if (pageNum) setPageNumber(pageNum);
  };

  const rowSubset = useMemo(() => {
    let rowsForTable;
    if (hasBackendPagination) {
      rowsForTable = [...rows];
    } else {
      rowsForTable = rows.slice(
        pageNumber * pageSize - pageSize,
        pageNumber * pageSize
      );
    }
    return rowsForTable;
  }, [hasBackendPagination, pageNumber, pageSize, rows, type]);

  useEffect(() => {
    // this is to prevent resetting page number when dropdown selection is open
    if (type === 'areas-units' && isAdminAreaUpdatesOnly) {
      return;
    }
    if (!hasBackendPagination) {
      setPageNumber(1);
    }
  }, [rows, isAdminAreaUpdatesOnly, hasBackendPagination, type]);

  useEffect(() => {
    if (hasBackendPagination) {
      switch (type) {
        case 'stoplight':
          if (pageNumber !== currentStoplightPage)
            setPageNumber(currentStoplightPage);
          break;
        case 'issue':
          if (pageNumber !== currentIssuePage) setPageNumber(currentIssuePage);
          break;
        case 'feedback':
          if (pageNumber !== currentFeedbackPage)
            setPageNumber(currentFeedbackPage);
          break;
        case 'patientRoundsReport':
          if (pageNumber !== currentPatientRoundsReportPage)
            setPageNumber(currentPatientRoundsReportPage);
          break;
        case 'patientCensusReport':
          if (pageNumber !== currentCensusReportPage)
            setPageNumber(currentCensusReportPage);
          break;
        case 'employeeRoundsReport':
          if (pageNumber !== currentEmployeeRoundsReportPage)
            setPageNumber(currentEmployeeRoundsReportPage);
          break;
        case 'employeeFrequencyReport':
          if (pageNumber !== currentEmployeeFreqReportPage)
            setPageNumber(currentEmployeeFreqReportPage);
          break;
        case 'users':
          if (pageNumber !== currentUsersPage) setPageNumber(currentUsersPage);
          break;
        default:
          break;
      }
    }
  }, [
    pageNumber,
    currentStoplightPage,
    currentIssuePage,
    currentFeedbackPage,
    currentPatientRoundsReportPage,
    currentCensusReportPage,
    currentEmployeeRoundsReportPage,
    currentEmployeeFreqReportPage,
    currentUsersPage
  ]);
  return (
    <>
      {loading ? (
        <Loading centered={false} label={loadingLabel} />
      ) : isTableEmpty &&
        (type === 'directEmployeeList' || type === 'delegateEmployeeList') ? (
        <NoDataSplashView selectedTab={selectedTab} type={type} />
      ) : isTableEmptyCompleted ? (
        <NoDataSplashView completed type={type} />
      ) : isTableEmptySearch ? (
        <NoDataSplashView search={isTableEmptySearch} type={type} />
      ) : isTableEmpty ? (
        <NoDataSplashView type={type} />
      ) : (
        <NeuTable
          className={`h-100 neu-text-gray-80 ${customClass}`}
          kind="default"
          sticky="header"
          id="Shared-Table"
          onNeuSort={onSort}
        >
          <TableHeader className="neu-bg-plain-0" unit="header" size="default">
            <StyledNeuTableRow unit="row" height="3.5rem">
              {headers}
            </StyledNeuTableRow>
          </TableHeader>
          <TableContent unit="content" offset={offset}>
            <TableContentContainer>{rowSubset}</TableContentContainer>
          </TableContent>
        </NeuTable>
      )}
      {!loading &&
        ((!totalResults && rows.length > pageSize) ||
          (!!totalResults && totalResults > pageSize)) && (
          <TableFooter>
            <StyledLabel style={{ fontWeight: 600, marginRight: '5px' }}>
              {totalResults && `Total Results: ${totalResults}`}
            </StyledLabel>
            <NeuPagination
              kind="paginator"
              className="me-3"
              // TODO: How can we reduce some code with page control
              current={pageNumber}
              limit={pageSize}
              results={!totalResults ? rows.length : totalResults}
              onNeuChange={e => pageNumberChanged(e.detail.value)}
            />
            <NeuPagination
              kind="jump"
              className="ms-2 mx-4"
              // TODO: How can we reduce some code with page control
              current={pageNumber}
              limit={pageSize}
              results={!totalResults ? rows.length : totalResults}
              onNeuChange={e => pageNumberChanged(e.detail.value)}
            />
          </TableFooter>
        )}
    </>
  );
};

const mapReduxStateToProps = (state: RootState) => ({
  currentStoplightPage: state.StoplightReducer.currentStoplightPage,
  currentIssuePage: state.TaskReducer.currentIssuePage,
  currentFeedbackPage: state.TaskReducer.currentFeedbackPage,
  currentPatientRoundsReportPage:
    state.ReportReducer.currentPatientRoundsReportPage,
  currentCensusReportPage: state.ReportReducer.currentCensusReportPage,
  currentEmployeeRoundsReportPage:
    state.ReportReducer.currentEmployeeRoundsReportPage,
  currentEmployeeFreqReportPage:
    state.ReportReducer.currentEmployeeFreqReportPage,
  currentUsersPage: state.AdminReducer.currentUsersPage
});

export default connect(mapReduxStateToProps)(Table);
